import { getAccessToken } from "../lib/auth";
import { apiBaseUrl } from "../lib/config";

const baseUrl = apiBaseUrl();

export function apiUrl(path: string): string {
  return /^https?:\/\//i.test(path) ? path : `${baseUrl}${path}`;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
  ) {
    super(`品哨 API 请求失败（状态码 ${status}）`);
  }
}

function authHeaders(): Record<string, string> {
  const token = getAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function requestJson<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${baseUrl}${path}`, {
    ...init,
    headers: {
      Accept: "application/json",
      ...authHeaders(),
      ...init?.headers,
    },
    credentials: "include",
  });
  const body = await response.json().catch(() => null);
  if (!response.ok) throw new ApiError(response.status, body);
  return body as T;
}

export function getJson<T>(path: string): Promise<T> {
  return requestJson<T>(path);
}

export function postJson<T>(path: string, body: unknown, idempotencyKey: string): Promise<T> {
  return requestJson<T>(path, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Idempotency-Key": idempotencyKey,
    },
    body: JSON.stringify(body),
  });
}

export function postForm<T>(path: string, body: FormData, idempotencyKey: string): Promise<T> {
  return requestJson<T>(path, {
    method: "POST",
    headers: { "Idempotency-Key": idempotencyKey },
    body,
  });
}

export function patchJson<T>(path: string, body: unknown, idempotencyKey: string): Promise<T> {
  return requestJson<T>(path, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      "Idempotency-Key": idempotencyKey,
    },
    body: JSON.stringify(body),
  });
}

export function putJson<T>(path: string, body: unknown, idempotencyKey: string): Promise<T> {
  return requestJson<T>(path, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      "Idempotency-Key": idempotencyKey,
    },
    body: JSON.stringify(body),
  });
}

export async function deleteJson(path: string, idempotencyKey: string): Promise<void> {
  const response = await fetch(`${baseUrl}${path}`, {
    method: "DELETE",
    headers: {
      Accept: "application/json",
      "Idempotency-Key": idempotencyKey,
      ...authHeaders(),
    },
    credentials: "include",
  });
  if (!response.ok) throw new ApiError(response.status, await response.json().catch(() => null));
}

export async function putBinary(path: string, content: Blob): Promise<void> {
  const response = await fetch(apiUrl(path), {
    method: "PUT",
    body: content,
    headers: {
      ...authHeaders(),
    },
    credentials: "include",
  });
  if (!response.ok) throw new ApiError(response.status, await response.json().catch(() => null));
}
